import { normalize } from 'normalizr'
import { Schemas } from '@redux/schema'
import { deleteEntity, updateEntities } from '@redux/entities'

import api from '@functions/api'

import type { SavedArtworkPageModel } from '@models/types'
import type { AppDispatch } from '@redux/store'
import type {
  Action, DeleteParams, EntityOptions, Errors, FetchRequestPayload, RootReducerState, UpdateParams,
} from '@redux/modules/types'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/savedArtworkPage/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/savedArtworkPage/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/savedArtworkPage/FETCH_FAILURE'

const CREATE_REQUEST = 'realbase/savedArtworkPage/CREATE_REQUEST'
const CREATE_SUCCESS = 'realbase/savedArtworkPage/CREATE_SUCCESS'
const CREATE_FAILURE = 'realbase/savedArtworkPage/CREATE_FAILURE'

const UPDATE_REQUEST = 'realbase/savedArtworkPage/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realbase/savedArtworkPage/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realbase/savedArtworkPage/UPDATE_FAILURE'

const DELETE_REQUEST = 'realbase/savedArtworkPage/DELETE_REQUEST'
const DELETE_SUCCESS = 'realbase/savedArtworkPage/DELETE_SUCCESS'
const DELETE_FAILURE = 'realbase/savedArtworkPage/DELETE_FAILURE'

export type SavedArtworkPageModuleState = {
  creating: boolean,
  deleting: boolean,
  errors: string[],
  loaded: boolean,
  loadedForKeys: string[],
  loadedIds: number[],
  loading: boolean,
  updating: boolean,
}

// Initial State
const initialState: SavedArtworkPageModuleState = {
  creating: false,
  deleting: false,
  errors: [],
  loaded: false,
  loadedForKeys: [],
  loadedIds: [],
  loading: false,
  updating: false,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}) {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
    loadedIds: payload.loadedIds,
  }
}

export function fetchSuccess() {
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors: Errors = []) {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest() {
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess() {
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors: Errors = []) {
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest() {
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess() {
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors: Errors = []) {
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function deleteRequest() {
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess() {
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors: Errors = []) {
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Action Creators
export function loadSavedArtworkPages(options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const loadedForKeys = [...getState().savedArtworkPages.loadedForKeys]

    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/saved_artwork_pages.json', options)
      .then(({ data }: { data: SavedArtworkPageModel[] }) => {
        const normalizedJson = normalize(data, Schemas.SAVED_ARTWORK_PAGE_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function loadSavedArtworkPage(savedArtworkPageId: number, options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const state = getState()
    const loadedForKeys = [...state.savedArtworkPages.loadedForKeys]
    const loadedIds = [...state.savedArtworkPages.loadedIds]

    if (entityKey) loadedForKeys.push(entityKey)

    if (savedArtworkPageId && !loadedIds.includes(savedArtworkPageId)){
      loadedIds.push(savedArtworkPageId)
    }

    dispatch(fetchRequest({ loadedForKeys, loadedIds }))

    const promise = api(`/saved_artwork_pages/${savedArtworkPageId}.json`, options)
      .then(({ data }: { data: SavedArtworkPageModel }) => {
        const normalizedJson = normalize(data, Schemas.SAVED_ARTWORK_PAGE)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function createSavedArtworkPage(savedArtworkPage: Partial<SavedArtworkPageModel>, options: EntityOptions = {}) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      saved_artwork_page: savedArtworkPage,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/saved_artwork_pages.json', options, config)
      .then(({ data }: { data: SavedArtworkPageModel }) => {
        const normalizedJson = normalize(data, Schemas.SAVED_ARTWORK_PAGE)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function updateSavedArtworkPage(
  savedArtworkPage: UpdateParams<SavedArtworkPageModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      saved_artwork_page: savedArtworkPage,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/saved_artwork_pages/${savedArtworkPage.id}.json`, options, config)
      .then(({ data }: { data: SavedArtworkPageModel }) => {
        const normalizedJson = normalize(data, Schemas.SAVED_ARTWORK_PAGE)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function deleteSavedArtworkPage(savedArtworkPage: DeleteParams<SavedArtworkPageModel>) {
  const config = {
    method: 'DELETE',
  }

  return (dispatch: AppDispatch) => {
    dispatch(deleteRequest())

    const promise = api(`/saved_artwork_pages/${savedArtworkPage.id}.json`, {}, config)
      .then(({ data }: { data: { id: number } }) => {
        const normalizedJson = normalize(data, Schemas.SAVED_ARTWORK_PAGE)
        dispatch(deleteEntity(normalizedJson))
        dispatch(deleteSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(deleteFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

// Reducer
export default function reducer(
  state: SavedArtworkPageModuleState = initialState,
  action: Action = { type: '' },
): SavedArtworkPageModuleState {
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
        loadedIds: action.loadedIds || state.loadedIds,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors || [],
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors || [],
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors || [],
      }
    case DELETE_REQUEST:
      return {
        ...state,
        deleting: true,
      }
    case DELETE_SUCCESS:
      return {
        ...state,
        deleting: false,
        errors: [],
      }
    case DELETE_FAILURE:
      return {
        ...state,
        deleting: false,
        errors: action.errors || [],
      }
    default:
      return state
  }
}
